import type { Uuid } from '../types/api';
import type { Attachment, AttachmentDisplay, UploadHandle } from '../types/attachment';
import type { LiniyaHttpClient } from '../http/client';
import { ApiError, NetworkError } from '../http/errors';
import { multipartWithFile, queryString, type UploadFile } from '../platform';

export interface UploadRequest {
  file: UploadFile;
  fileName: string;
  onProgress?: (percent: number) => void;
}

interface TicketResponse {
  ticket: string;
  expires_in: number;
  has_thumbnail?: boolean;
  width?: number | null;
  height?: number | null;
}

/** How much of a ticket's life a minted URL may be reused for. */
const TICKET_REUSE_FRACTION = 0.8;

/** Held only until the real lifetime arrives with the server's answer. */
const PROVISIONAL_TTL_MS = 30_000;

/**
 * Attachment transfer.
 *
 * Uploads use `XMLHttpRequest` rather than `fetch` for one reason: it reports
 * how many bytes have actually left the machine. A progress bar that only
 * moves from 0 to 100 is worse than none on a slow mobile uplink, where a
 * large photo can take a minute.
 */
export class MediaApi {
  /**
   * Minted display URLs, keyed by the attachment they point at.
   *
   * Every ticket is different, so asking for one again produces a different
   * URL — and a different URL is a different entry to the browser, which
   * throws away the copy it already has and downloads the photo again. Reusing
   * the URL while its ticket is still valid is what lets the week-long cache
   * the server sets actually do anything: reopening a conversation then costs
   * nothing instead of refetching every picture in it.
   */
  private readonly displayUrls = new Map<
    string,
    { display: Promise<AttachmentDisplay>; expiresAt: number }
  >();

  constructor(
    private readonly http: LiniyaHttpClient,
    private readonly baseUrl = '',
  ) {}

  /**
   * A URL an `<img>`, `<video>` or `<a download>` can load on its own.
   *
   * Those elements cannot carry an Authorization header, so access is proven
   * by a short-lived ticket in the query string that is bound to this one
   * attachment and this one account. Keeping it a real URL rather than an
   * in-memory blob is what lets a video player issue byte-range requests
   * instead of downloading the whole file before it can start.
   */
  async displayUrl(attachmentId: Uuid, fileName: string): Promise<string> {
    return (await this.display(attachmentId, fileName)).url;
  }

  /**
   * Everything needed to show one attachment.
   *
   * `thumbnailUrl` addresses a downscaled copy the server derived on upload. A
   * photo from a phone is several thousand pixels across and is shown in a
   * bubble a few hundred wide, so sending the original costs megabytes to
   * deliver and decode for nothing. It is null when no such copy exists:
   * anything that is not an image, and ciphertext the server cannot read.
   *
   * `width` and `height` describe the original, so the space can be reserved
   * before the picture arrives rather than the conversation shifting as each
   * one lands.
   */
  async display(attachmentId: Uuid, fileName: string): Promise<AttachmentDisplay> {
    const key = `${attachmentId} ${fileName}`;
    const cached = this.displayUrls.get(key);
    if (cached && cached.expiresAt > Date.now()) return await cached.display;

    const display = this.mintDisplay(attachmentId, fileName);
    // Cached before it resolves, so several bubbles asking for the same file at
    // once share one ticket instead of minting one each.
    this.displayUrls.set(key, { display, expiresAt: Date.now() + PROVISIONAL_TTL_MS });
    try {
      return await display;
    } catch (error) {
      this.displayUrls.delete(key);
      throw error;
    }
  }

  /**
   * Forget every minted URL.
   *
   * A ticket is bound to the account that asked for it, so URLs cached for one
   * person would be refused after somebody else signs in on the same tab.
   */
  clearDisplayUrls(): void {
    this.displayUrls.clear();
  }

  private async mintDisplay(attachmentId: Uuid, fileName: string): Promise<AttachmentDisplay> {
    const response = await this.http.request<TicketResponse>(
      `/api/v1/attachments/${attachmentId}/ticket/`,
      { method: 'POST' },
    );
    const base = `${this.baseUrl}/api/v1/attachments/${attachmentId}/download/`;
    const ticket = { filename: fileName, ticket: response.ticket };
    const url = `${base}${queryString(ticket)}`;

    const display: AttachmentDisplay = {
      url,
      thumbnailUrl: response.has_thumbnail
        ? `${base}${queryString({ ...ticket, variant: 'thumb' })}`
        : null,
      width: response.width ?? null,
      height: response.height ?? null,
    };

    // Held for most of the ticket's life, stopping a little short of it so a
    // URL handed out now is still valid when the element gets round to loading
    // it. The margin is what the server itself reported, not a guess.
    const lifetimeMs = Math.max(response.expires_in, 60) * 1000;
    this.displayUrls.set(`${attachmentId} ${fileName}`, {
      display: Promise.resolve(display),
      expiresAt: Date.now() + lifetimeMs * TICKET_REUSE_FRACTION,
    });
    return display;
  }

  upload(request: UploadRequest): UploadHandle {
    const controller = new AbortController();
    const promise = this.performUpload(request, controller.signal);
    return { promise, cancel: () => controller.abort() };
  }

  private async performUpload(request: UploadRequest, signal: AbortSignal): Promise<Attachment> {
    const tokens = await this.http.currentTokens();
    return await new Promise<Attachment>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseUrl}/api/v1/attachments/upload/`);
      xhr.withCredentials = true;
      if (tokens?.accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${tokens.accessToken}`);
      }

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          request.onProgress?.((event.loaded / event.total) * 100);
        }
      });

      xhr.addEventListener('load', () => {
        let parsed: unknown = null;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          parsed = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          const body = parsed as { attachment?: Attachment } | null;
          if (body?.attachment) {
            resolve(body.attachment);
            return;
          }
          reject(new NetworkError('Сервер вернул неожиданный ответ на загрузку.'));
          return;
        }
        const errorBody = (parsed as { error?: { code: string; message: string } } | null)?.error;
        reject(
          new ApiError(xhr.status, {
            code: errorBody?.code ?? `http_${xhr.status}`,
            message: errorBody?.message ?? 'Не удалось загрузить вложение.',
            details: {},
            request_id: '',
          }),
        );
      });

      xhr.addEventListener('error', () =>
        reject(new NetworkError('Не удалось загрузить вложение.')),
      );
      xhr.addEventListener('abort', () => reject(new NetworkError('Загрузка отменена.')));
      signal.addEventListener('abort', () => xhr.abort(), { once: true });

      xhr.send(multipartWithFile('file', request.file, request.fileName));
    });
  }
}
